// The default chord for every command, per platform. The one place a key is named. A chord is
// modifiers and a key joined by `+`: `Mod+Shift+K`, `Enter`, `Mod+Alt+1`. `Mod` is Cmd on a
// Mac and Ctrl elsewhere. A string binds both platforms; an object binds each.
//
// These are the first candidates from docs/design/workspace-shell.md. docs/runs/keyboard-probe.md
// has not measured the browsers yet (its session stayed locked); its reading of Chromium's
// source says Ctrl+Alt chords are never used on Windows because they are AltGr on international
// keyboards, so insert-match takes its third candidate, Alt+N, there. When the probe runs, each
// row it finds unsafe moves to its next candidate here and nowhere else. Rows marked `editor`
// are chords the editor package binds itself; the shell shows them and never dispatches them.
//
// The machine translation chords are unmeasured candidates outside the design's table and the
// probe's reserved lists: Cmd+Option+M is macOS's minimise-all, so the Segment takes T; the
// document run has no Windows chord because Alt+Shift is the input-language switch there and
// Ctrl+Alt is AltGr, so it runs from the palette; Cmd+Shift+5 is the macOS screenshot tool,
// so the pane's focus chord skips to 6.
//
// The completion shortcut (docs/design/completion.md, decision 7) is unmeasured as well. The
// usual Space chords are taken on a Mac: Cmd+Space is Spotlight and Ctrl+Space switches input
// sources, which Thai translators use.
//
// The concordance and the Overview pane (docs/design/overview-and-concordance.md) are unmeasured
// candidates too. The search chords a translator knows are taken here: Mod+F is the browser's
// find, Mod+Shift+F the filter, and memoQ's Ctrl+K the palette. The Overview's focus chord takes
// the next number after the Machine pane's.
//
// The Block's level chords (docs/design/pdf-sourcing.md, decision 6) and the Page pane's focus
// are unmeasured candidates as well. Word's own outline chords are taken here: Alt+Shift switches
// the input language on Windows, Option+Shift+arrows select by word on a Mac, and Cmd+[ and Cmd+]
// are back and forward in a Mac browser, so the level steps sit on the period and comma keys.
//
// Find and replace (docs/design/find-and-replace.md, decision 10) is unmeasured too: Mod+F opens
// the bar in place of the browser's find, which cannot see rows the virtualized views leave out,
// and Mod+G and Mod+Shift+G step while it is open. Mod+Shift+G is also add-to-glossary; the find's
// command comes first in the registry and holds only while the bar is open, so the glossary keeps
// the chord the rest of the time.
//
// Moving the rest of a target to the next Segment (ADR 0023) is Cmd+Return on a Mac and
// Ctrl+Enter on Windows, unmeasured. The editor's keymap takes it before Enter, so a confirm never
// fires. Undo and redo reach back across Segments (round three of the design pass, item 6): Mod+Z
// and Mod+Shift+Z (Ctrl+Y on Windows) take the editor's own steps first and then the session's
// stack, and fall through to the editor or a field while the stack has nothing.
//
// Join and split (docs/design/split-and-join.md, decision 7) are unmeasured and flagged for the
// probe: Cmd+Option+Down and Cmd+Option+Enter on a Mac, Alt+Down and Alt+Enter on Windows, where
// Ctrl+Alt is AltGr. The next-Segment and next-unreviewed rows above already hold Mod+Down and
// Mod+Shift+Enter. The split picker's Enter, Shift+Enter, Left and Right act only while it is
// open, and its commands come first in the registry so they win those keys there.
//
// The settings on Mod+Comma (docs/design/ux-review.md, unit 1) are unmeasured and flagged for the
// probe: it is the settings chord of most Mac applications, and Chrome's own on a Mac, so the probe
// must show a page can take it before a translator relies on it; the project menu lists the
// settings either way.
//
// The full map of chords on Mod+Slash (unit 6) is unmeasured too: it is the shortcuts chord of
// Google Docs and Slack, and the status bar's Shortcuts button opens the same map.
//
// Up and Down walk a pane's items and Enter inserts the one in focus (unit 15); they hold only
// while an item of a pane has the focus, so the editor and every field keep their arrows.
//
// The menu bar's chord (docs/design/menu-bar.md, decision 10) is Google Docs' pair, Alt+F on
// Windows and Ctrl+Option+F on a Mac, unmeasured: Ctrl+Option is VoiceOver's modifier, so the
// probe measures it with VoiceOver on. Shift+Tab from the title row reaches the bar without it.
//
// The file chords, Mod+N, Mod+O, Mod+W and Mod+S, are the browser's own (a window, a file, the
// tab, the page), so only the native menu bar binds them; rows marked `native` exist only there.
//
// The Edit menu's six chords are the editor's and every field's own, so they are marked `editor`:
// the menu shows them and the shell never takes them from a field.

import { chordIn, chordTable, editorChordIn, type ChordOverrides, type ChordSpec, type Platform } from './bindings.ts'

export type { ChordOverrides, ChordRow, Platform } from './bindings.ts'

const TABLE: Record<string, ChordSpec> = {
  'palette.open': 'Mod+K',
  'segment.confirm': 'Enter',
  'segment.next': 'Mod+Down',
  'segment.previous': 'Mod+Up',
  'segment.nextUntranslated': 'Mod+J',
  'segment.nextUnreviewed': 'Mod+Shift+Enter',
  'match.insert1': { mac: 'Mod+Alt+1', windows: 'Alt+1' },
  'match.insert2': { mac: 'Mod+Alt+2', windows: 'Alt+2' },
  'match.insert3': { mac: 'Mod+Alt+3', windows: 'Alt+3' },
  'match.insert4': { mac: 'Mod+Alt+4', windows: 'Alt+4' },
  'match.insert5': { mac: 'Mod+Alt+5', windows: 'Alt+5' },
  'match.insertFocused': 'Enter',
  'glossary.insertFocused': 'Enter',
  'pane.next': 'Down',
  'pane.previous': 'Up',
  'editor.placeMissing': 'Mod+Shift+O',
  'editor.complete': 'Mod+Shift+K',
  'glossary.add': 'Mod+Shift+G',
  'view.switch': 'Mod+Shift+V',
  'segment.markLeftEmpty': 'Mod+Shift+E',
  'segment.keepSource': 'Mod+Shift+S',
  'segment.approve': 'Mod+Shift+A',
  'segment.unlock': 'Mod+Shift+A',
  'segment.joinNext': { mac: 'Mod+Alt+Down', windows: 'Alt+Down' },
  'segment.split': { mac: 'Mod+Alt+Enter', windows: 'Alt+Enter' },
  'segment.moveTail': 'Mod+Enter',
  'segment.undo': 'Mod+Z',
  'segment.redo': { mac: 'Mod+Shift+Z', windows: 'Mod+Y' },
  'split.cut': 'Enter',
  'split.cutKeep': 'Shift+Enter',
  'split.previous': 'Left',
  'split.next': 'Right',
  'flag.needsReview': 'Mod+Shift+R',
  'block.levelDeeper': 'Mod+Shift+Period',
  'block.levelShallower': 'Mod+Shift+Comma',
  'memory.search': 'Mod+Shift+U',
  'machine.translateSegment': { mac: 'Mod+Alt+T', windows: 'Alt+T' },
  'machine.translateBlock': { mac: 'Mod+Alt+B', windows: 'Alt+B' },
  'machine.translateDocument': { mac: 'Mod+Alt+Shift+T', windows: null },
  'machine.insertFocused': 'Enter',
  'filter.open': 'Mod+Shift+F',
  'find.open': 'Mod+F',
  'find.next': 'Mod+G',
  'find.previous': 'Mod+Shift+G',
  'escape': 'Escape',
  'settings.open': 'Mod+Comma',
  'shortcuts.open': 'Mod+Slash',
  'menubar.focus': { mac: 'Ctrl+Alt+F', windows: 'Alt+F' },
  'project.new': { mac: 'Mod+N', windows: 'Mod+N', native: true },
  'project.openBundle': { mac: 'Mod+O', windows: 'Mod+O', native: true },
  'project.close': { mac: 'Mod+W', windows: 'Mod+W', native: true },
  'project.save': { mac: 'Mod+S', windows: 'Mod+S', native: true },
  'edit.undo': { mac: 'Mod+Z', windows: 'Mod+Z', editor: true },
  'edit.redo': { mac: 'Mod+Shift+Z', windows: 'Mod+Y', editor: true },
  'edit.cut': { mac: 'Mod+X', windows: 'Mod+X', editor: true },
  'edit.copy': { mac: 'Mod+C', windows: 'Mod+C', editor: true },
  'edit.paste': { mac: 'Mod+V', windows: 'Mod+V', editor: true },
  'edit.selectAll': { mac: 'Mod+A', windows: 'Mod+A', editor: true },
  'format.Bold': { mac: 'Mod+B', windows: 'Mod+B', editor: true },
  'format.Italic': { mac: 'Mod+I', windows: 'Mod+I', editor: true },
}

export const CHORDS = chordTable(TABLE)

export function chordFor(id: string, platform: Platform, overrides: ChordOverrides = {}, menus: 'page' | 'system' = 'page'): string | null {
  return chordIn(CHORDS, id, platform, overrides, menus)
}

export function isEditorChord(id: string): boolean {
  return editorChordIn(CHORDS, id)
}
